/**
 * Hook para gerenciamento de Comentários em Atividades
 * Suporta @menções e vinculação ao tenant
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

// Logger do módulo
const logger = createLogger('ActivityComments');

/**
 * Interface do comentário
 */
export interface ActivityComment {
  id: string;
  tenant_id: string;
  activity_id: string;
  content: string;
  author_id: string;
  created_at: string;
  updated_at: string;
  // Relacionamentos carregados
  author?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  mentions?: ActivityMention[];
}

/**
 * Interface da menção
 */
export interface ActivityMention {
  id: string;
  comment_id: string;
  mentioned_user_id: string;
  read_at: string | null;
  created_at: string;
  // Relacionamento carregado
  mentioned_user?: {
    id: string;
    full_name: string | null;
    avatar_url: string | null;
  } | null;
}

/**
 * Interface para criar comentário
 */
export interface CreateCommentInput {
  content: string;
  mentionedUserIds?: string[];
}

/**
 * Regex para extrair menções do texto (@nome)
 */
const MENTION_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Extrai IDs de usuários mencionados do texto
 * Formato esperado: @[Nome do Usuário](user_id)
 */
export function extractMentionIds(content: string): string[] {
  const matches = content.matchAll(MENTION_REGEX);
  const ids: string[] = [];
  for (const match of matches) {
    if (match[2]) {
      ids.push(match[2]);
    }
  }
  return ids;
}

/**
 * Formata texto com menções para exibição
 * Converte @[Nome](id) para <span class="mention">@Nome</span>
 * Usa cor azul de link para destacar menções no texto
 */
export function formatMentionsForDisplay(content: string): string {
  return content.replace(MENTION_REGEX, '<span class="text-blue-600 dark:text-blue-400 font-medium">@$1</span>');
}

/**
 * Remove IDs das menções para exibição em textarea de edição
 * Converte @[Nome](uuid) → @Nome
 */
export function stripMentionIds(content: string): string {
  return content.replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1');
}

/**
 * Extrai mapa de nome → uuid das menções no conteúdo original
 * Usado para reconstruir menções após edição
 */
export function extractMentionMap(content: string): Record<string, string> {
  const map: Record<string, string> = {};
  const regex = /@\[([^\]]+)\]\(([^)]+)\)/g;
  let match;
  while ((match = regex.exec(content)) !== null) {
    map[match[1]] = match[2];
  }
  return map;
}

/**
 * Hook para comentários de uma atividade específica
 */
export function useActivityComments(activityId: string | null) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();
  
  // Ref para rastrear mutations locais e evitar double-refetch
  const localMutationRef = useRef<number | null>(null);

  // Query para buscar comentários
  const commentsQuery = useQuery({
    queryKey: ['activity-comments', activityId],
    queryFn: async () => {
      if (!activityId || !currentTenant?.id) return [];

      logger.debug('Buscando comentários da atividade via RPC server-side:', activityId);

      // Server-side: RPC retorna comentários + autor + menções aninhados em JSONB
      // Substitui 3 SELECTs diretos (crm_activity_comments + profiles + crm_activity_mentions)
      const { data, error } = await supabase.rpc('get_activity_comments', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar comentários:', error.message);
        throw error;
      }

      // Mapeia retorno da RPC para o shape público do hook
      return (data || []).map((row: any) => ({
        id: row.id,
        tenant_id: row.tenant_id,
        activity_id: row.activity_id,
        content: row.content,
        author_id: row.author_id,
        created_at: row.created_at,
        updated_at: row.updated_at,
        author: row.author ?? null,
        mentions: Array.isArray(row.mentions) ? row.mentions : [],
      })) as ActivityComment[];
    },
    enabled: !!activityId && !!currentTenant?.id,
    staleTime: 30 * 1000,
  });

  // =========================================================================
  // REALTIME: Escuta mudanças em comentários da atividade
  // =========================================================================
  useEffect(() => {
    // Só ativa se tiver activityId e tenant
    if (!activityId || !currentTenant?.id) return;

    logger.debug('Iniciando canal Realtime para comentários:', activityId);

    // Cria canal específico para esta atividade
    const channelName = `activity-comments:${activityId}`;
    
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'crm_activity_comments',
          filter: `activity_id=eq.${activityId}`,
        },
        (payload) => {
          logger.debug('Evento Realtime recebido:', payload.eventType);
          
          // Se foi mutation local recente (< 2s), ignora para evitar double-refetch
          if (localMutationRef.current) {
            const elapsed = Date.now() - localMutationRef.current;
            if (elapsed < 2000) {
              logger.debug('Ignorando evento Realtime - mutation local recente');
              localMutationRef.current = null;
              return;
            }
          }
          
          // Invalida cache para forçar refetch imediato
          queryClient.invalidateQueries({ 
            queryKey: ['activity-comments', activityId],
            refetchType: 'active'
          });
          
          // Também atualiza contagem no feed principal
          queryClient.invalidateQueries({ 
            queryKey: ['crm-activities-feed'],
            refetchType: 'active'
          });
        }
      )
      .subscribe((status) => {
        logger.debug(`Canal ${channelName} status:`, status);
      });

    // Cleanup ao desmontar ou mudar activityId
    return () => {
      logger.debug('Removendo canal Realtime:', channelName);
      supabase.removeChannel(channel);
    };
  }, [activityId, currentTenant?.id, queryClient]);

  // Mutation para adicionar comentário
  const addComment = useMutation({
    onMutate: () => {
      // Marca timestamp para evitar double-refetch do Realtime
      localMutationRef.current = Date.now();
    },
    mutationFn: async (input: CreateCommentInput) => {
      if (!activityId || !currentTenant?.id || !user?.id) {
        throw new Error('Contexto inválido para adicionar comentário');
      }

      logger.debug('Adicionando comentário:', { activityId, content: input.content });

      // 1. Inserir comentário
      const { data: comment, error: commentError } = await supabase
        .from('crm_activity_comments')
        .insert({
          tenant_id: currentTenant.id,
          activity_id: activityId,
          content: input.content,
          author_id: user.id,
        })
        .select('*')
        .single();

      if (commentError) {
        logger.error('Erro ao criar comentário:', commentError.message);
        throw commentError;
      }

      // 2. Extrair e inserir menções
      const mentionIds = input.mentionedUserIds || extractMentionIds(input.content);
      
      if (mentionIds.length > 0) {
        const mentions = mentionIds.map(userId => ({
          comment_id: comment.id,
          mentioned_user_id: userId,
        }));

        const { error: mentionError } = await supabase
          .from('crm_activity_mentions')
          .insert(mentions);

        if (mentionError) {
          logger.warn('Erro ao inserir menções:', mentionError.message);
          // Não falha a operação por causa de menções
        }
      }

      // Busca profile do autor
      const { data: authorProfile } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url')
        .eq('id', user.id)
        .single();

      return {
        ...comment,
        author: authorProfile || null,
      } as ActivityComment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['activity-comments', activityId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({ 
        queryKey: ['crm-activities-feed'],
        refetchType: 'active'
      });
      toast.success('Comentário adicionado!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao adicionar comentário:', error.message);
      toast.error(`Erro ao adicionar comentário: ${error.message}`);
    },
  });

  // Mutation para deletar comentário
  const deleteComment = useMutation({
    onMutate: () => {
      // Marca timestamp para evitar double-refetch do Realtime
      localMutationRef.current = Date.now();
    },
    mutationFn: async (commentId: string) => {
      logger.debug('Removendo comentário:', commentId);

      const { error } = await supabase
        .from('crm_activity_comments')
        .delete()
        .eq('id', commentId)
        .eq('author_id', user?.id); // Só pode deletar próprio comentário

      if (error) {
        logger.error('Erro ao remover comentário:', error.message);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['activity-comments', activityId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({ 
        queryKey: ['crm-activities-feed'],
        refetchType: 'active'
      });
      toast.success('Comentário removido!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao remover comentário:', error.message);
      toast.error(`Erro ao remover comentário: ${error.message}`);
    },
  });

  // =========================================================================
  // Mutation para marcar menção como lida
  // Atualiza crm_activity_mentions.read_at e agent_notifications.is_read
  // =========================================================================
  const markMentionAsRead = useMutation({
    mutationFn: async (mentionId: string) => {
      if (!user?.id) {
        throw new Error('Usuário não autenticado');
      }

      logger.debug('Marcando menção como lida:', mentionId);

      // 1. Atualiza read_at na menção
      const { error: mentionError } = await supabase
        .from('crm_activity_mentions')
        .update({ read_at: new Date().toISOString() })
        .eq('id', mentionId)
        .eq('mentioned_user_id', user.id);

      if (mentionError) {
        logger.error('Erro ao marcar menção como lida:', mentionError.message);
        throw mentionError;
      }

      // 2. Marca notificação relacionada como lida (usa RPC existente)
      // Busca notificação pelo mention_id no metadata
      const { data: notification } = await supabase
        .from('agent_notifications')
        .select('id')
        .eq('user_id', user.id)
        .eq('type', 'activity_mention')
        .contains('metadata', { mention_id: mentionId })
        .maybeSingle();

      if (notification) {
        await supabase.rpc('mark_notifications_as_read', {
          p_notification_ids: [notification.id],
          p_mark_all: false,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: ['activity-comments', activityId],
        refetchType: 'active'
      });
      // Invalida notificações para atualizar badge do sino
      queryClient.invalidateQueries({ 
        queryKey: ['agent-notifications'],
        refetchType: 'active'
      });
      toast.success('Menção marcada como lida!');
    },
    onError: (error: Error) => {
      logger.error('Falha ao marcar menção como lida:', error.message);
      toast.error(`Erro ao marcar menção: ${error.message}`);
    },
  });

  return {
    comments: commentsQuery.data || [],
    isLoading: commentsQuery.isLoading,
    error: commentsQuery.error,
    refetch: commentsQuery.refetch,
    addComment,
    deleteComment,
    markMentionAsRead,
  };
}

/**
 * Hook para buscar membros do tenant (para @menções)
 */
/**
 * Hook para buscar membros do tenant (para @menções)
 * Reutiliza RPC existente para garantir segurança e consistência
 */
export function useTenantMembers() {
  const { currentTenant } = useAuth();

  return useQuery({
    queryKey: ['tenant-members-for-mention', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      logger.debug('Buscando membros do tenant via RPC para @menções');

      // Reutiliza RPC existente que já retorna os campos necessários
      const { data, error } = await supabase.rpc('get_tenant_members_for_audit', {
        p_tenant_id: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar membros do tenant:', error.message);
        throw error;
      }

      // Filtra apenas membros ativos e formata para uso no popover de menções
      return (data || [])
        .filter((member: any) => member.is_active)
        .map((member: any) => ({
          id: member.user_id,
          full_name: member.full_name || 'Usuário',
          email: member.email || null,
          avatar_url: member.avatar_url || null,
        }));
    },
    enabled: !!currentTenant?.id,
    staleTime: 5 * 60 * 1000, // Cache de 5 minutos
  });
}
